import { apiClient } from './api-client';

// ==================== Enums ====================

export enum AuditAction {
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  LOGIN_FAILED = 'LOGIN_FAILED',
  PASSWORD_CHANGE = 'PASSWORD_CHANGE',
  PASSWORD_RESET = 'PASSWORD_RESET',
  MFA_ENABLED = 'MFA_ENABLED',
  MFA_DISABLED = 'MFA_DISABLED',
  EXPORT = 'EXPORT',
  IMPORT = 'IMPORT',
  BULK_UPDATE = 'BULK_UPDATE',
  BULK_DELETE = 'BULK_DELETE',
  ASSIGN = 'ASSIGN',
  UNASSIGN = 'UNASSIGN',
  MERGE = 'MERGE',
  SETTINGS_CHANGE = 'SETTINGS_CHANGE',
  PERMISSION_CHANGE = 'PERMISSION_CHANGE',
  API_KEY_CREATED = 'API_KEY_CREATED',
  API_KEY_REVOKED = 'API_KEY_REVOKED',
  INTEGRATION_CONNECTED = 'INTEGRATION_CONNECTED',
  INTEGRATION_DISCONNECTED = 'INTEGRATION_DISCONNECTED',
}

export enum AuditResource {
  TICKET = 'ticket',
  CUSTOMER = 'customer',
  USER = 'user',
  ARTICLE = 'article',
  WORKFLOW = 'workflow',
  INTEGRATION = 'integration',
  SETTINGS = 'settings',
  TENANT = 'tenant',
}

export enum AuditStatus {
  SUCCESS = 'success',
  FAILURE = 'failure',
  ERROR = 'error',
}

// ==================== Interfaces ====================

export interface AuditLog {
  id: string;
  tenantId: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  userRole?: string;
  actorType: string;
  action: string;
  resource: string;
  resourceId?: string;
  resourceName?: string;
  oldValue?: Record<string, any>;
  newValue?: Record<string, any>;
  changedFields: string[];
  ipAddress?: string;
  userAgent?: string;
  metadata: Record<string, any>;
  tags: string[];
  status: string;
  errorMessage?: string;
  createdAt: string;
}

export interface AuditLogsFilter {
  startDate?: string;
  endDate?: string;
  action?: AuditAction;
  resource?: string;
  resourceId?: string;
  userId?: string;
  userEmail?: string;
  status?: AuditStatus;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedAuditLogs {
  data: AuditLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

export interface AuditStats {
  totalLogs: number;
  todayLogs: number;
  weekLogs: number;
  monthLogs: number;
  byAction: Record<string, number>;
  byResource: Record<string, number>;
  byStatus: Record<string, number>;
  topUsers: Array<{ userId: string; userEmail: string; count: number }>;
  failedActions: number;
  securityEvents: number;
}

/**
 * Aggregates over the SAME filtered set `getAuditLogs` pages through, so a
 * screen can state a fact about the whole trail instead of about the rows it
 * happens to hold. `topUsers` is top-10; `byActorType` is complete.
 */
export interface AuditSummary {
  total: number;
  byResource: Record<string, number>;
  byStatus: Record<string, number>;
  byActorType: Record<string, number>;
  topUsers: Array<{
    actorType: string;
    userId?: string | null;
    userName?: string | null;
    userEmail?: string | null;
    count: number;
  }>;
}

export interface AuditAlert {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  isActive: boolean;
  conditions: Array<{
    field: string;
    operator: 'equals' | 'contains' | 'in' | 'not_in' | 'regex';
    value: any;
  }>;
  threshold: number;
  timeWindow: number;
  notifyEmail: string[];
  notifySlack?: string;
  notifyWebhook?: string;
  lastTriggeredAt?: string;
  triggerCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAuditAlertInput {
  name: string;
  description?: string;
  conditions: Array<{
    field: string;
    operator: 'equals' | 'contains' | 'in' | 'not_in' | 'regex';
    value: any;
  }>;
  threshold?: number;
  timeWindow?: number;
  notifyEmail?: string[];
  notifySlack?: string;
  notifyWebhook?: string;
}

export interface AuditExport {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  fileUrl?: string;
  recordCount: number;
  error?: string;
}

export interface LoginAttempt {
  id: string;
  email: string;
  userId?: string;
  success: boolean;
  failureReason?: string;
  mfaRequired: boolean;
  mfaMethod?: string;
  mfaSuccess?: boolean;
  ipAddress: string;
  userAgent?: string;
  location?: Record<string, any>;
  createdAt: string;
}

export interface DataAccessLog {
  id: string;
  userId: string;
  userEmail?: string;
  dataType: string;
  resourceType: string;
  resourceId: string;
  fieldsAccessed: string[];
  accessType: string;
  ipAddress?: string;
  reason?: string;
  createdAt: string;
}

// ==================== Service ====================

class AuditService {
  // Audit Logs
  async getAuditLogs(filter: AuditLogsFilter = {}): Promise<PaginatedAuditLogs> {
    const params = new URLSearchParams();

    if (filter.startDate) params.append('startDate', filter.startDate);
    if (filter.endDate) params.append('endDate', filter.endDate);
    if (filter.action) params.append('action', filter.action);
    if (filter.resource) params.append('resource', filter.resource);
    if (filter.resourceId) params.append('resourceId', filter.resourceId);
    if (filter.userId) params.append('userId', filter.userId);
    if (filter.userEmail) params.append('userEmail', filter.userEmail);
    if (filter.status) params.append('status', filter.status);
    if (filter.search) params.append('search', filter.search);
    if (filter.page) params.append('page', String(filter.page));
    if (filter.limit) params.append('limit', String(filter.limit));
    if (filter.sortBy) params.append('sortBy', filter.sortBy);
    if (filter.sortOrder) params.append('sortOrder', filter.sortOrder);

    const queryString = params.toString();
    const response = await apiClient.get<PaginatedAuditLogs>(
      `/audit/logs${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  async getAuditLogById(id: string): Promise<AuditLog> {
    const response = await apiClient.get<AuditLog>(`/audit/logs/${id}`);
    return response.data;
  }

  /**
   * Counts for the current filter, with no paging params: the point of this
   * call is the number the page cannot reach by counting rows.
   */
  async getAuditSummary(filter: AuditLogsFilter = {}): Promise<AuditSummary> {
    const params = new URLSearchParams();

    if (filter.startDate) params.append('startDate', filter.startDate);
    if (filter.endDate) params.append('endDate', filter.endDate);
    if (filter.action) params.append('action', filter.action);
    if (filter.resource) params.append('resource', filter.resource);
    if (filter.resourceId) params.append('resourceId', filter.resourceId);
    if (filter.userId) params.append('userId', filter.userId);
    if (filter.userEmail) params.append('userEmail', filter.userEmail);
    if (filter.status) params.append('status', filter.status);
    if (filter.search) params.append('search', filter.search);

    const queryString = params.toString();
    const response = await apiClient.get<AuditSummary>(
      `/audit/logs/summary${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  async getAuditStats(): Promise<AuditStats> {
    const response = await apiClient.get<AuditStats>('/audit/logs/stats');
    return response.data;
  }

  async getResourceHistory(
    resource: string,
    resourceId: string,
    limit?: number
  ): Promise<AuditLog[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await apiClient.get<AuditLog[]>(
      `/audit/resource/${resource}/${resourceId}${params}`
    );
    return response.data;
  }

  async getUserActivity(userId: string, limit?: number): Promise<AuditLog[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await apiClient.get<AuditLog[]>(
      `/audit/user/${userId}/activity${params}`
    );
    return response.data;
  }

  // Export
  async createExport(options: {
    format: 'csv' | 'json' | 'xlsx';
    startDate?: string;
    endDate?: string;
    action?: AuditAction;
    resource?: string;
    userId?: string;
  }): Promise<{ exportId: string }> {
    const response = await apiClient.post<{ exportId: string }>(
      '/audit/export',
      options
    );
    return response.data;
  }

  async getExportStatus(exportId: string): Promise<AuditExport> {
    const response = await apiClient.get<AuditExport>(
      `/audit/export/${exportId}`
    );
    return response.data;
  }

  // Alerts
  async getAlerts(): Promise<AuditAlert[]> {
    const response = await apiClient.get<AuditAlert[]>('/audit/alerts');
    return response.data;
  }

  async createAlert(input: CreateAuditAlertInput): Promise<AuditAlert> {
    const response = await apiClient.post<AuditAlert>('/audit/alerts', input);
    return response.data;
  }

  async updateAlert(
    alertId: string,
    input: Partial<CreateAuditAlertInput>
  ): Promise<AuditAlert> {
    const response = await apiClient.put<AuditAlert>(
      `/audit/alerts/${alertId}`,
      input
    );
    return response.data;
  }

  async deleteAlert(alertId: string): Promise<void> {
    await apiClient.delete(`/audit/alerts/${alertId}`);
  }

  async toggleAlert(alertId: string, isActive: boolean): Promise<AuditAlert> {
    const response = await apiClient.put<AuditAlert>(
      `/audit/alerts/${alertId}/toggle?active=${isActive}`
    );
    return response.data;
  }

  async getAlertHistory(alertId: string, limit?: number): Promise<any[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await apiClient.get<any[]>(
      `/audit/alerts/${alertId}/history${params}`
    );
    return response.data;
  }

  // Login Attempts
  async getLoginAttempts(options?: {
    email?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }): Promise<LoginAttempt[]> {
    const params = new URLSearchParams();
    if (options?.email) params.append('email', options.email);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.limit) params.append('limit', String(options.limit));

    const queryString = params.toString();
    const response = await apiClient.get<LoginAttempt[]>(
      `/audit/login-attempts${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  // Data Access Logs
  async getDataAccessLogs(options?: {
    dataType?: string;
    userId?: string;
    resourceId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }): Promise<DataAccessLog[]> {
    const params = new URLSearchParams();
    if (options?.dataType) params.append('dataType', options.dataType);
    if (options?.userId) params.append('userId', options.userId);
    if (options?.resourceId) params.append('resourceId', options.resourceId);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.limit) params.append('limit', String(options.limit));

    const queryString = params.toString();
    const response = await apiClient.get<DataAccessLog[]>(
      `/audit/data-access${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  // Helpers
  getActionLabel(action: string): string {
    const labels: Record<string, string> = {
      CREATE: 'Created',
      READ: 'Viewed',
      UPDATE: 'Updated',
      DELETE: 'Deleted',
      LOGIN: 'Logged In',
      LOGOUT: 'Logged Out',
      LOGIN_FAILED: 'Login Failed',
      PASSWORD_CHANGE: 'Password Changed',
      PASSWORD_RESET: 'Password Reset',
      MFA_ENABLED: 'MFA Enabled',
      MFA_DISABLED: 'MFA Disabled',
      EXPORT: 'Exported',
      IMPORT: 'Imported',
      BULK_UPDATE: 'Bulk Updated',
      BULK_DELETE: 'Bulk Deleted',
      ASSIGN: 'Assigned',
      UNASSIGN: 'Unassigned',
      MERGE: 'Merged',
      SETTINGS_CHANGE: 'Settings Changed',
      PERMISSION_CHANGE: 'Permissions Changed',
      API_KEY_CREATED: 'API Key Created',
      API_KEY_REVOKED: 'API Key Revoked',
      INTEGRATION_CONNECTED: 'Integration Connected',
      INTEGRATION_DISCONNECTED: 'Integration Disconnected',
    };
    return labels[action] || action;
  }

  getActionColor(action: string): string {
    const colors: Record<string, string> = {
      CREATE: 'bg-green-100 text-green-700',
      UPDATE: 'bg-blue-100 text-blue-700',
      DELETE: 'bg-red-100 text-red-700',
      LOGIN: 'bg-purple-100 text-purple-700',
      LOGOUT: 'bg-gray-100 text-gray-700',
      LOGIN_FAILED: 'bg-red-100 text-red-700',
      EXPORT: 'bg-indigo-100 text-indigo-700',
      IMPORT: 'bg-indigo-100 text-indigo-700',
      SETTINGS_CHANGE: 'bg-yellow-100 text-yellow-700',
      PERMISSION_CHANGE: 'bg-orange-100 text-orange-700',
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      success: 'bg-green-100 text-green-700',
      failure: 'bg-red-100 text-red-700',
      error: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  }

  formatTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  }
}

export const auditService = new AuditService();
export default auditService;
